import { Button, Modal, Skeleton, Tooltip } from '@scale/scaleui';
import { Attachment, ModelInputFileAttachmentType } from '@scale/shared/types/chat';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useChatThemeStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import apiFetch from 'react-scale/lib/api-fetch';
import { getAudioSource } from 'react-scale/lib/chat-api/multimodal';

type ShowAttachmentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  attachment: Attachment;
};

const ShowAttachmentModal = ({ isOpen, onClose, attachment }: ShowAttachmentModalProps) => {
  const theme = useChatThemeStore(state => state.theme);

  const { data, isError } = useQuery(
    ['chat-attachment', attachment.url],
    async (): Promise<{ url: string } | undefined> => {
      if (!attachment.url) {
        return undefined;
      }

      // we have a special function get get audio files
      // this is currently just to avoid fetching stale data from the CDN
      if (attachment.fileType === ModelInputFileAttachmentType.Audio) {
        const { source } = await getAudioSource({ audioUrl: attachment.url });
        return { url: source };
      }

      return await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
        method: 'POST',
        body: { url: attachment?.url },
      });
    },
    {
      staleTime: 23 * 60 * 60 * 1_000, // 23 hours
      enabled: !!attachment.url,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const onImgLoad = () => {
    if (data === undefined) return;

    const img = new Image();
    img.src = data?.url;
    setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  // attachment.originalUrl is present if image mirroring is enabled.
  // Otherwise, the original url is stored in attachment.url.
  const originalUrl = attachment.originalUrl ?? attachment.url ?? '';
  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => {
          onClose();
        }}
        theme={theme}
      >
        {isError && <div>Error fetching file</div>}
        {attachment.fileType === ModelInputFileAttachmentType.Image ? (
          <>
            {attachment.base64EncodedFileBytes ? (
              <img
                src={`data:image/png;base64,${attachment.base64EncodedFileBytes}`}
                alt={attachment.name}
                onLoad={onImgLoad}
              />
            ) : data?.url.length ? (
              <img src={data?.url} alt={attachment.name} onLoad={onImgLoad} />
            ) : (
              <img src={attachment.url} alt={attachment.name} onLoad={onImgLoad} />
            )}
            <div className="italic">
              Width: {dimensions.width}px, Height: {dimensions.height}px
            </div>
            {originalUrl && (
              <Button
                className="mt-2"
                variant={'secondary'}
                onClick={() => {
                  navigator.clipboard.writeText(originalUrl);
                }}
              >
                Copy original URL
              </Button>
            )}
          </>
        ) : attachment.fileType === ModelInputFileAttachmentType.Audio ? (
          data === undefined ? (
            <Skeleton className="animate-pulse" />
          ) : (
            <audio controls src={data.url} />
          )
        ) : (
          <div>
            Visualization not yet supported for type: {attachment.mimeType}
            <br />
            <br />
            {data !== undefined && <DownloadButton label="Download File" url={data.url} />}
          </div>
        )}
      </Modal>
    </>
  );
};

function DownloadButton({ label, url }: { label: string; url: string }) {
  const handleClick = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip
      text="These will be clickable in the real Delivery row"
      target={
        <Button className="bg-neutral-50 flex gap-1" variant="neutral" onClick={handleClick}>
          <FontAwesomeIcon icon="download" />
          {label}
        </Button>
      }
    />
  );
}

export default ShowAttachmentModal;
